import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  MULTI_SELECT_ALL,
  isMultiSelectAll,
  isMultiSelectNone,
  multiSelectSummary,
} from '../utils/multiSelectFilterState';

const PANEL_HEAD_H = 40;

export default function MultiSelectFilter({
  label,
  items = [],
  selectedIds = MULTI_SELECT_ALL,
  onChange,
  getLabel = (item) => item.name,
  getId = (item) => item.id,
  placeholder = 'الكل',
  maxHeight = 240,
  panelZIndex = 3000,
}) {
  const [open, setOpen] = useState(false);
  const [panelPos, setPanelPos] = useState(null);
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return undefined;
    function updatePos() {
      const rect = triggerRef.current.getBoundingClientRect();
      const listMax = Math.max(120, maxHeight - PANEL_HEAD_H);
      const spaceBelow = window.innerHeight - rect.bottom - 8;
      const spaceAbove = rect.top - 8;
      const openUp = spaceBelow < 160 && spaceAbove > spaceBelow;
      const available = openUp ? spaceAbove : spaceBelow;
      const listHeight = Math.min(listMax, Math.max(120, available - PANEL_HEAD_H));
      setPanelPos({
        top: openUp ? rect.top - listHeight - PANEL_HEAD_H - 4 : rect.bottom + 4,
        left: rect.left,
        width: Math.max(rect.width, 220),
        listHeight,
      });
    }
    updatePos();
    window.addEventListener('resize', updatePos);
    window.addEventListener('scroll', updatePos, true);
    return () => {
      window.removeEventListener('resize', updatePos);
      window.removeEventListener('scroll', updatePos, true);
    };
  }, [open, maxHeight, items.length]);

  useEffect(() => {
    function handleClick(e) {
      if (wrapRef.current?.contains(e.target)) return;
      const panel = document.getElementById('multi-select-panel-root');
      if (panel?.contains(e.target)) return;
      setOpen(false);
    }
    function handleKey(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, []);

  const allIds = items.map(getId);
  const allMode = isMultiSelectAll(selectedIds, allIds.length);
  const noneMode = isMultiSelectNone(selectedIds);
  const partialIds = Array.isArray(selectedIds) ? selectedIds : [];

  function isChecked(id) {
    if (noneMode) return false;
    if (allMode) return true;
    return partialIds.includes(id);
  }

  function toggle(id) {
    if (allMode) {
      onChange(allIds.filter((x) => x !== id));
      return;
    }
    if (noneMode) {
      onChange([id]);
      return;
    }
    if (partialIds.includes(id)) {
      onChange(partialIds.filter((x) => x !== id));
      return;
    }
    const next = [...partialIds, id];
    onChange(next.length >= allIds.length ? MULTI_SELECT_ALL : next);
  }

  const summary = multiSelectSummary(selectedIds, items.length, placeholder);

  const panel = open && panelPos ? createPortal(
    <div
      id="multi-select-panel-root"
      style={{
        position: 'fixed',
        top: panelPos.top,
        left: panelPos.left,
        width: panelPos.width,
        zIndex: panelZIndex,
        background: '#fff',
        border: '1px solid #e0e0e0',
        borderRadius: '10px',
        boxShadow: '0 10px 32px rgba(0,0,0,0.15)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div style={styles.panelHead}>
        <button type="button" style={styles.link} onClick={() => onChange(MULTI_SELECT_ALL)}>تحديد الكل</button>
        <button type="button" style={styles.link} onClick={() => onChange([])}>مسح</button>
      </div>
      <div style={{ ...styles.list, maxHeight: panelPos.listHeight }}>
        {items.length === 0 ? (
          <p style={styles.empty}>لا توجد خيارات</p>
        ) : items.map((item) => {
          const id = getId(item);
          return (
            <label key={id} style={styles.item}>
              <input type="checkbox" checked={isChecked(id)} onChange={() => toggle(id)} />
              <span>{getLabel(item)}</span>
            </label>
          );
        })}
      </div>
    </div>,
    document.body,
  ) : null;

  return (
    <div style={styles.wrap} ref={wrapRef}>
      <label style={styles.label}>{label}</label>
      <button
        ref={triggerRef}
        type="button"
        style={{ ...styles.trigger, ...(open ? styles.triggerOpen : {}) }}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span style={styles.summaryText}>{summary}</span>
        <span style={styles.chevron}>{open ? '▲' : '▼'}</span>
      </button>
      {panel}
    </div>
  );
}

const styles = {
  wrap: { position: 'relative', width: '100%', minWidth: 0 },
  label: { display: 'block', fontSize: '12px', fontWeight: '600', color: '#555', marginBottom: '4px' },
  trigger: {
    width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '9px 12px', border: '1px solid #ddd', borderRadius: '8px', background: '#fff',
    cursor: 'pointer', fontSize: '13px', color: '#333', textAlign: 'right', minHeight: '40px',
    boxSizing: 'border-box',
  },
  triggerOpen: { borderColor: '#5B3E96', boxShadow: '0 0 0 2px rgba(91,62,150,0.12)' },
  summaryText: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 },
  chevron: { fontSize: '10px', color: '#888', marginRight: '6px', flexShrink: 0 },
  panelHead: {
    display: 'flex', justifyContent: 'space-between', padding: '8px 12px',
    borderBottom: '1px solid #eee', background: '#fafafa', flexShrink: 0,
    minHeight: PANEL_HEAD_H, boxSizing: 'border-box', alignItems: 'center',
  },
  link: {
    border: 'none', background: 'none', color: '#3E2C6E', cursor: 'pointer',
    fontSize: '12px', fontWeight: '600',
  },
  list: {
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '6px 8px',
    WebkitOverflowScrolling: 'touch',
    overscrollBehaviorX: 'contain',
    overscrollBehaviorY: 'auto',
  },
  item: {
    display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 6px',
    fontSize: '13px', cursor: 'pointer', borderRadius: '6px',
  },
  empty: { margin: '8px', fontSize: '12px', color: '#999', textAlign: 'center' },
};